/**
 * Real-time Price Alert Monitor
 *
 * Subscribes to PumpPortal WebSocket for tokens with active alerts
 * and triggers notifications when conditions are met.
 */

import { useEffect, useRef, useCallback } from 'react';
import { pumpPortal } from '../lib/websocket';
import type { PumpEvent, PumpTrade } from '../lib/websocket';
import {
  getActiveAlerts,
  checkAlertTrigger,
  sendAlertNotification,
  type PriceAlert,
} from '../lib/alerts';

interface UseAlertMonitorOptions {
  walletAddress: string | null;
  enabled?: boolean;
  onAlertTriggered?: (alert: PriceAlert) => void;
}

// Calculate SOL price from bonding curve state
function calculatePriceFromTrade(trade: PumpTrade): number {
  if (trade.vTokensInBondingCurve === 0) return 0;
  return trade.vSolInBondingCurve / trade.vTokensInBondingCurve / 1e9;
}

export function useAlertMonitor({
  walletAddress,
  enabled = true,
  onAlertTriggered,
}: UseAlertMonitorOptions) {
  const alertsRef = useRef<PriceAlert[]>([]);
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const triggeredAlertsRef = useRef<Set<string>>(new Set());

  // Update alerts list
  const refreshAlerts = useCallback(() => {
    if (!walletAddress) {
      alertsRef.current = [];
      return [];
    }
    const alerts = getActiveAlerts(walletAddress);
    alertsRef.current = alerts;
    return alerts;
  }, [walletAddress]);

  // Handle incoming trade events
  const handleTrade = useCallback((event: PumpEvent) => {
    if (event.txType !== 'buy' && event.txType !== 'sell') return;

    const trade = event as PumpTrade;
    const tokenAddress = trade.mint;
    const currentPrice = calculatePriceFromTrade(trade);

    // Check all alerts for this token
    for (const alert of alertsRef.current) {
      if (alert.tokenAddress !== tokenAddress) continue;
      if (alert.status !== 'active') continue;
      if (triggeredAlertsRef.current.has(alert.id)) continue;

      // Check if alert should trigger
      if (checkAlertTrigger(alert, currentPrice)) {
        // Mark as triggered to avoid duplicate notifications
        triggeredAlertsRef.current.add(alert.id);

        // Update alert with current price for notification
        const triggeredAlert = { ...alert, currentPrice, status: 'triggered' as const };

        // Send browser notification
        sendAlertNotification(triggeredAlert);

        // Callback for UI updates
        onAlertTriggered?.(triggeredAlert);

        console.log('[AlertMonitor] Alert triggered:', alert.tokenSymbol, currentPrice);
      }
    }
  }, [onAlertTriggered]);

  // Subscribe to token trades for alerted tokens
  const subscribe = useCallback(async () => {
    const alerts = refreshAlerts();
    if (alerts.length === 0) return;

    // Get unique token addresses
    const tokenAddresses = [...new Set(alerts.map(a => a.tokenAddress))];

    if (tokenAddresses.length === 0) return;

    try {
      await pumpPortal.connect();
      unsubscribeRef.current = pumpPortal.subscribeTokenTrades(tokenAddresses, handleTrade);
      console.log('[AlertMonitor] Subscribed to', tokenAddresses.length, 'tokens');
    } catch (error) {
      console.error('[AlertMonitor] Subscribe error:', error);
    }
  }, [refreshAlerts, handleTrade]);

  // Unsubscribe and resubscribe when alerts change
  const resubscribe = useCallback(() => {
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }
    subscribe();
  }, [subscribe]);

  useEffect(() => {
    if (!enabled || !walletAddress) {
      return;
    }

    subscribe();

    // Refresh alerts periodically in case user creates new ones
    const interval = setInterval(() => {
      const newAlerts = refreshAlerts();
      const currentMints = new Set(alertsRef.current.map(a => a.tokenAddress));
      const newMints = new Set(newAlerts.map(a => a.tokenAddress));

      // Check if token list changed
      const changed = newMints.size !== currentMints.size ||
        [...newMints].some(m => !currentMints.has(m));

      if (changed) {
        console.log('[AlertMonitor] Alerts changed, resubscribing...');
        resubscribe();
      }
    }, 10000); // Check every 10 seconds

    return () => {
      clearInterval(interval);
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [enabled, walletAddress, subscribe, refreshAlerts, resubscribe]);

  return {
    refreshAlerts,
    resubscribe,
  };
}
